import {
    UserManager,
    type UserManagerSettings,
    type IWindow,
    type NavigateResponse,
    type CreateSignoutRequestArgs,
} from 'oidc-client-ts';

/**
 * Extended UserManager for AWS Cognito.
 *
 * Cognito's logout endpoint is non-standard:
 *   https://{domain}/logout?client_id={id}&logout_uri={uri}
 *
 * This class overrides the signout flow to build the correct URL
 * instead of using the OIDC end_session_endpoint.
 */
export class CognitoUserManager extends UserManager {
    private readonly cognitoDomain: string;

    constructor(settings: UserManagerSettings) {
        super(settings);

        const domain = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;
        if (!domain) throw new Error('Missing VITE_COGNITO_DOMAIN');

        this.cognitoDomain = domain.startsWith('http')
            ? domain.replace(/\/+$/, '')
            : `https://${domain}`;
    }

    async _signoutStart(
        _args: CreateSignoutRequestArgs | undefined,
        handle: IWindow
    ): Promise<NavigateResponse> {
        const logger = this._logger.create('_signoutStart');

        try {
            const user = await this._loadUser();
            logger.debug('loaded current user from storage');

            if (this.settings.revokeTokensOnSignout) {
                await this._revokeInternal(user);
            }

            await this.removeUser();
            logger.debug('user removed, building Cognito logout URL');

            const logoutUrl = new URL(`${this.cognitoDomain}/logout`);
            logoutUrl.searchParams.set('client_id', this.settings.client_id);
            logoutUrl.searchParams.set(
                'logout_uri',
                this.settings.post_logout_redirect_uri ?? `${window.location.origin}/logout`
            );

            logger.debug('navigating to:', logoutUrl.toString());

            // Build a minimal signin request to obtain a state id for the handle
            const signinRequest = await this._client.createSigninRequest({
                request_type: 'si:r',
            });

            return await handle.navigate({
                url: logoutUrl.toString(),
                state: signinRequest.state.id,
                response_mode: signinRequest.state.response_mode,
                scriptOrigin: this.settings.iframeScriptOrigin,
            });
        } catch (err) {
            logger.debug('error during signout, closing navigator window');
            handle.close();
            throw err;
        }
    }
}
